'use client';
import { useEffect, useRef, useState } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { clsx } from 'clsx';
import { resolveLabelColor, TagColorSwatches, type TagColorId } from '@paeffgen/react/crm-documents/ui';
import type { FavoriteLabel } from '@paeffgen/react/crm-documents/data-access-graphql';

export interface TagAssignContentProps {
  /** Alle Labels des Kunden (in sortOrder-Reihenfolge). */
  labels: FavoriteLabel[];
  /** Labels, die aktuell am Artikel hängen. */
  activeLabels: string[];
  onToggle: (label: string) => void;
  onCreate: (name: string, color: TagColorId) => void;
}

export function TagAssignContent({ labels, activeLabels, onToggle, onCreate }: TagAssignContentProps) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<TagColorId>('blue');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (adding) inputRef.current?.focus(); }, [adding]);

  const commit = () => {
    const t = name.trim();
    if (!t) return;
    onCreate(t, color);
    setName(''); setColor('blue'); setAdding(false);
  };

  return (
    <div className="px-4 py-3">
      <div className="flex flex-wrap gap-2">
        {labels.map((l) => {
          const on = activeLabels.includes(l.name);
          return (
            <button
              key={l.name}
              type="button"
              aria-pressed={on}
              onClick={() => onToggle(l.name)}
              className={clsx(
                'inline-flex items-center gap-1.5 h-9 px-3 rounded-full border text-[13px] font-semibold transition-colors',
                on ? 'bg-primary border-primary text-white' : 'bg-white border-gray-200 text-secondary hover:bg-gray-50',
              )}
            >
              <span className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: on ? 'rgba(255,255,255,0.85)' : resolveLabelColor(l.color, l.name) }} />
              {l.name}
              {on && <Check size={13} strokeWidth={3} />}
            </button>
          );
        })}
        {labels.length === 0 && (
          <p className="text-[13px] text-text-muted italic py-1">Noch keine Labels — unten anlegen.</p>
        )}
      </div>

      {adding ? (
        <div className="mt-3 rounded-xl border border-gray-200 p-3 space-y-3">
          <TagColorSwatches value={color} onChange={setColor} />
          <div className="flex items-center gap-2">
            <input ref={inputRef} value={name} maxLength={40} placeholder="Neues Label…"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') setAdding(false); }}
              className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-gray-200 bg-white outline-none text-[15px] text-secondary placeholder:text-gray-400 focus:border-primary focus:ring-1 focus:ring-primary" />
            <button type="button" onClick={() => setAdding(false)} aria-label="Abbrechen"
              className="shrink-0 h-10 w-10 inline-flex items-center justify-center rounded-lg text-text-muted hover:bg-gray-100"><X size={18} /></button>
            <button type="button" onClick={commit} disabled={!name.trim()}
              className="shrink-0 h-10 px-4 inline-flex items-center justify-center rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary-hover disabled:opacity-40 disabled:cursor-not-allowed">Erstellen</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => setAdding(true)}
          className="mt-3 w-full h-11 inline-flex items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-dashed border-primary/60 text-primary text-sm font-semibold hover:border-primary hover:bg-orange-50 transition-colors">
          <Plus size={16} /> Neues Label
        </button>
      )}
    </div>
  );
}
